import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Play,
  Clock,
  Cog,
  RefreshCw,
  Calendar,
  AlertCircle,
  CheckCircle2,
  X
} from 'lucide-react';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function CurrentActionsPage() {
  const [actions, setActions] = useState([]);
  const [machines, setMachines] = useState([]);
  const [ilots, setIlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(new Date());
  const [filter, setFilter] = useState(null); // null | 'current' | 'upcoming' | 'recent'

  useEffect(() => {
    fetchData();
    // Update current time every minute
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [actionsRes, machinesRes, ilotsRes] = await Promise.all([
        fetch(`${API}/actions`, { credentials: 'include' }),
        fetch(`${API}/machines`, { credentials: 'include' }),
        fetch(`${API}/ilots`, { credentials: 'include' })
      ]);

      if (actionsRes.ok) setActions(await actionsRes.json());
      if (machinesRes.ok) setMachines(await machinesRes.json());
      if (ilotsRes.ok) setIlots(await ilotsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleFilterClick = (newFilter) => {
    setFilter(filter === newFilter ? null : newFilter);
  };

  // Filter actions that are currently in progress
  // Compare using timestamps to avoid timezone issues
  const currentActions = actions.filter(action => {
    const start = parseISO(action.start_time);
    const end = parseISO(action.end_time);
    const nowTime = now.getTime();
    return nowTime >= start.getTime() && nowTime <= end.getTime();
  });

  // Filter actions starting soon (within next 2 hours)
  const upcomingActions = actions.filter(action => {
    const start = parseISO(action.start_time);
    const nowTime = now.getTime();
    const diffMs = start.getTime() - nowTime;
    const diffMinutes = diffMs / (1000 * 60);
    return diffMinutes > 0 && diffMinutes <= 120;
  }).sort((a, b) => parseISO(a.start_time).getTime() - parseISO(b.start_time).getTime());

  // Filter actions that ended recently (within last 2 hours)
  const recentActions = actions.filter(action => {
    const end = parseISO(action.end_time);
    const nowTime = now.getTime();
    const diffMs = nowTime - end.getTime();
    const diffMinutes = diffMs / (1000 * 60);
    return diffMinutes > 0 && diffMinutes <= 120;
  }).sort((a, b) => parseISO(b.end_time).getTime() - parseISO(a.end_time).getTime());

  const getMachine = (machineId) => machines.find(m => m.machine_id === machineId);
  const getIlot = (ilotId) => ilots.find(i => i.ilot_id === ilotId);

  const getProgress = (action) => {
    const start = parseISO(action.start_time);
    const end = parseISO(action.end_time);
    const totalMinutes = differenceInMinutes(end, start);
    const elapsedMinutes = differenceInMinutes(now, start);
    return Math.min(100, Math.max(0, (elapsedMinutes / totalMinutes) * 100));
  };

  const getRemainingTime = (action) => {
    const end = parseISO(action.end_time);
    const remaining = differenceInMinutes(end, now);
    if (remaining <= 0) return 'Terminé';
    if (remaining < 60) return `${remaining} min`;
    const hours = Math.floor(remaining / 60);
    const mins = remaining % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const getTimeUntilStart = (action) => {
    const start = parseISO(action.start_time);
    const minutes = differenceInMinutes(start, now);
    if (minutes < 60) return `Dans ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `Dans ${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const getTimeSinceEnd = (action) => {
    const end = parseISO(action.end_time);
    const minutes = differenceInMinutes(now, end);
    if (minutes < 60) return `Il y a ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `Il y a ${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="current-actions-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  // Determine which actions to show based on filter
  const showCurrent = !filter || filter === 'current';
  const showUpcoming = !filter || filter === 'upcoming';
  const showRecent = !filter || filter === 'recent';

  return (
    <div className="space-y-6" data-testid="current-actions-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight uppercase">
            Actions en Cours
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Suivi en temps réel des opérations
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="font-mono text-2xl font-bold">
              {format(now, 'HH:mm', { locale: fr })}
            </p>
            <p className="text-xs text-muted-foreground font-mono uppercase">
              {format(now, 'EEEE d MMMM', { locale: fr })}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-sm border-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats Cards - Clickable */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card
          className={`border-2 rounded-sm cursor-pointer transition-all hover:scale-[1.02] ${
            filter === 'current'
              ? 'border-green-500 ring-2 ring-green-500/30 bg-green-100 dark:bg-green-900/40'
              : 'border-green-500/50 bg-green-50 dark:bg-green-950/20 hover:border-green-500'
          }`}
          onClick={() => handleFilterClick('current')}
          data-testid="filter-current"
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-green-500 flex items-center justify-center">
                <Play className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-3xl font-bold text-green-700 dark:text-green-400">
                  {currentActions.length}
                </p>
                <p className="text-sm text-green-600 dark:text-green-500 font-mono uppercase">
                  En cours
                </p>
              </div>
              {filter === 'current' && (
                <X className="w-5 h-5 text-green-600" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border-2 rounded-sm cursor-pointer transition-all hover:scale-[1.02] ${
            filter === 'upcoming'
              ? 'border-orange-500 ring-2 ring-orange-500/30 bg-orange-100 dark:bg-orange-900/40'
              : 'border-orange-500/50 bg-orange-50 dark:bg-orange-950/20 hover:border-orange-500'
          }`}
          onClick={() => handleFilterClick('upcoming')}
          data-testid="filter-upcoming"
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-orange-500 flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-3xl font-bold text-orange-700 dark:text-orange-400">
                  {upcomingActions.length}
                </p>
                <p className="text-sm text-orange-600 dark:text-orange-500 font-mono uppercase">
                  À venir (2h)
                </p>
              </div>
              {filter === 'upcoming' && (
                <X className="w-5 h-5 text-orange-600" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border-2 rounded-sm cursor-pointer transition-all hover:scale-[1.02] ${
            filter === 'recent'
              ? 'border-blue-500 ring-2 ring-blue-500/30 bg-blue-100 dark:bg-blue-900/40'
              : 'border-blue-500/50 bg-blue-50 dark:bg-blue-950/20 hover:border-blue-500'
          }`}
          onClick={() => handleFilterClick('recent')}
          data-testid="filter-recent"
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-blue-500 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">
                  {recentActions.length}
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-500 font-mono uppercase">
                  Terminées (2h)
                </p>
              </div>
              {filter === 'recent' && (
                <X className="w-5 h-5 text-blue-600" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter indicator */}
      {filter && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="rounded-sm px-3 py-1">
            Filtre actif : {filter === 'current' ? 'En cours' : filter === 'upcoming' ? 'À venir' : 'Terminées'}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilter(null)}
            className="h-7 px-2"
          >
            <X className="w-4 h-4 mr-1" />
            Effacer
          </Button>
        </div>
      )}

      {/* Current Actions */}
      {showCurrent && (
        <Card className="border-2 rounded-sm">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-heading font-semibold text-lg uppercase tracking-tight flex items-center gap-2">
              <Play className="w-5 h-5 text-green-500" />
              Actions en Cours ({currentActions.length})
            </CardTitle>
            <CardDescription className="font-mono text-xs uppercase tracking-wider">
              Opérations actuellement en exécution
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {currentActions.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Aucune action en cours</p>
              </div>
            ) : (
              <div className="space-y-4">
                {currentActions.map((action) => {
                  const machine = getMachine(action.machine_id);
                  const ilot = machine ? getIlot(machine.ilot_id) : null;
                  const progress = getProgress(action);
                  const remaining = getRemainingTime(action);

                  return (
                    <div
                      key={action.action_id}
                      className="p-4 border-2 border-green-200 dark:border-green-800 rounded-sm bg-green-50/50 dark:bg-green-950/20"
                      data-testid={`current-action-${action.action_id}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{action.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="rounded-sm">
                              <Cog className="w-3 h-3 mr-1" />
                              {machine?.name || 'Machine inconnue'}
                            </Badge>
                            {ilot && (
                              <Badge variant="secondary" className="rounded-sm">
                                {ilot.name}
                              </Badge>
                            )}
                          </div>
                          {action.description && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {action.description}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm text-muted-foreground">
                            {format(parseISO(action.start_time), 'HH:mm', { locale: fr })} - {format(parseISO(action.end_time), 'HH:mm', { locale: fr })}
                          </p>
                          <p className="font-mono text-lg font-bold text-green-600 dark:text-green-400">
                            {remaining}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Progression</span>
                          <span>{Math.round(progress)}%</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming and Recent - Layout changes based on filter */}
      {(showUpcoming || showRecent) && (
        <div className={`grid gap-6 ${showUpcoming && showRecent ? 'lg:grid-cols-2' : ''}`}>
          {/* Upcoming Actions */}
          {showUpcoming && (
            <Card className="border-2 rounded-sm">
              <CardHeader className="border-b border-border">
                <CardTitle className="font-heading font-semibold text-lg uppercase tracking-tight flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-500" />
                  À Venir ({upcomingActions.length})
                </CardTitle>
                <CardDescription className="font-mono text-xs uppercase tracking-wider">
                  Prochaines 2 heures
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {upcomingActions.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Aucune action prévue</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upcomingActions.map((action) => {
                      const machine = getMachine(action.machine_id);
                      const ilot = machine ? getIlot(machine.ilot_id) : null;
                      const timeUntil = getTimeUntilStart(action);

                      return (
                        <div
                          key={action.action_id}
                          className="p-3 border rounded-sm bg-orange-50/50 dark:bg-orange-950/10 border-orange-200 dark:border-orange-800"
                          data-testid={`upcoming-action-${action.action_id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{action.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-xs text-muted-foreground truncate">
                                  {machine?.name} • {format(parseISO(action.start_time), 'HH:mm', { locale: fr })}
                                </p>
                                {ilot && filter === 'upcoming' && (
                                  <Badge variant="secondary" className="rounded-sm text-xs">
                                    {ilot.name}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="rounded-sm text-orange-600 border-orange-300 flex-shrink-0">
                              {timeUntil}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recent Actions */}
          {showRecent && (
            <Card className="border-2 rounded-sm">
              <CardHeader className="border-b border-border">
                <CardTitle className="font-heading font-semibold text-lg uppercase tracking-tight flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-500" />
                  Terminées ({recentActions.length})
                </CardTitle>
                <CardDescription className="font-mono text-xs uppercase tracking-wider">
                  Dernières 2 heures
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {recentActions.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Aucune action récente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentActions.map((action) => {
                      const machine = getMachine(action.machine_id);
                      const ilot = machine ? getIlot(machine.ilot_id) : null;
                      const timeSince = getTimeSinceEnd(action);

                      return (
                        <div
                          key={action.action_id}
                          className="p-3 border rounded-sm bg-blue-50/50 dark:bg-blue-950/10 border-blue-200 dark:border-blue-800"
                          data-testid={`recent-action-${action.action_id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{action.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-xs text-muted-foreground truncate">
                                  {machine?.name}
                                </p>
                                {ilot && filter === 'recent' && (
                                  <Badge variant="secondary" className="rounded-sm text-xs">
                                    {ilot.name}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="rounded-sm text-blue-600 border-blue-300 flex-shrink-0">
                              {timeSince}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
